import type { ApiClient } from '@carbonwebmail/api-client'
import type { TemplateDto } from '@carbonwebmail/contracts'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { Alert } from '../../ui/Alert.js'
import { Async } from '../../ui/Async.js'
import { count } from '../../ui/format.js'
import { useMay } from '../../ui/yourRole.js'
import { TemplateCard } from './TemplateCard.js'
import { TemplateUpload } from './TemplateUpload.js'
import type { Kind } from './useTemplates.js'
import { useTemplates } from './useTemplates.js'

/**
 * The templates somebody has saved. A library, and nothing else.
 *
 * **Nothing is built here.** Designing an email happens in Compose, which is a
 * screen of its own because laying one out needs the width. This screen answers
 * one question — which saved designs do I have — and every action on it is
 * about a design that already exists: use it, open it, copy it, delete it.
 *
 * It was a gallery *and* an editor, and the editor was a builder squeezed into a
 * form column. Two places to build one thing is two places for the builder's
 * configuration and the save path to drift, and neither of them was usable.
 *
 * A list of names stops working the moment there are a dozen, because what
 * people remember about a template is what it looked like. So it is a grid of
 * thumbnails and the name is the caption.
 */
export function TemplatesPage({ client }: { readonly client: ApiClient }) {
  const templates = useTemplates({ client })
  const { state, kind, problem, notice, busy } = templates
  const navigate = useNavigate()
  /** Whether the upload box is open. Nothing outside this screen can observe it. */
  const [uploading, setUploading] = useState(false)

  /*
   * A viewer browses the library and adds nothing to it.
   *
   * The sidebar already hides Compose and Design with AI from them; this is
   * the same decision for the three ways into Compose that live on this
   * screen, plus the two actions on a card.
   */
  const mayWrite = useMay('templates', 'write')

  return (
    <>
      <header className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-bold text-[22px] text-t1 -tracking-[0.01em]">Templates</h1>
          <p className="mt-1 text-[13px] text-t2">
            Designs worth keeping. A campaign takes a copy, so editing a template never changes a
            campaign that has already gone out.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/*
            An upload is a design that already exists, which is most people's
            first one — but it still opens in Compose rather than landing in the
            gallery unseen, so there is one way a template gets written.
          */}
          {!mayWrite ? null : (
            <>
              <button
                type="button"
                onClick={() => setUploading(true)}
                className="rounded-pill border border-line px-4 py-2.5 font-semibold text-[14px] text-t2 hover:text-t1"
              >
                Upload a template
              </button>
              {/* The only way to make one, and it leads to the screen that makes them. */}
              <Link
                className="rounded-pill bg-brand px-5 py-2.5 font-bold text-[14px] text-brand-contrast"
                to="/compose/template/new"
              >
                New template
              </Link>
            </>
          )}
        </div>
      </header>

      {problem === undefined ? null : <Alert>{problem}</Alert>}
      {notice === undefined ? null : <Alert tone="ok">{notice}</Alert>}

      <Async state={state} skeleton={<GallerySkeleton />} onRetry={() => void templates.retry()}>
        {(all) =>
          all.templates.length === 0 ? (
            <Empty mayWrite={mayWrite} />
          ) : (
            <>
              <div className="mb-4 flex flex-wrap items-center gap-2">
                {KINDS.map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={kind === value}
                    onClick={() => templates.setKind(value)}
                    className={`rounded-pill border px-3 py-1 font-medium text-[12px] ${
                      kind === value
                        ? 'border-brand bg-brand-soft text-brand'
                        : 'border-line bg-surface text-t2'
                    }`}
                  >
                    {label} {count(countOf(all.templates, value))}
                  </button>
                ))}
              </div>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {shown(all.templates, kind).map((template) => (
                  <TemplateCard
                    busy={busy}
                    key={template.templateId}
                    mayWrite={mayWrite}
                    onDuplicate={() => void templates.duplicate(template)}
                    onRemove={() => void templates.remove(template)}
                    template={template}
                  />
                ))}
              </div>
            </>
          )
        }
      </Async>

      {uploading ? (
        <TemplateUpload
          onClose={() => setUploading(false)}
          onOpen={(imported) => navigate('/compose/template/new', { state: { imported } })}
        />
      ) : null}
    </>
  )
}

/** The filters are the two kinds a body can be. There is no third. */
const KINDS: readonly (readonly [Kind, string])[] = [
  ['all', 'All'],
  ['designed', 'Designs'],
  ['prose', 'Plain text'],
]

function shown(all: readonly TemplateDto[], kind: Kind): readonly TemplateDto[] {
  return kind === 'all' ? all : all.filter((template) => template.body.kind === kind)
}

function countOf(all: readonly TemplateDto[], kind: Kind): number {
  return shown(all, kind).length
}

/** The shape of what is coming, rather than a spinner. */
function GallerySkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {[0, 1, 2, 3].map((slot) => (
        <div
          className="h-[320px] animate-pulse rounded-card border border-line bg-surface"
          key={slot}
        />
      ))}
    </div>
  )
}

function Empty({ mayWrite }: { readonly mayWrite: boolean }) {
  return (
    <div className="rounded-card border border-line bg-surface p-12 text-center">
      <h2 className="font-semibold text-[16px] text-t1">No templates yet</h2>
      <p className="mx-auto mt-2 max-w-sm text-[13px] text-t2">
        {mayWrite
          ? 'A template is a design you would rather not build twice. Compose one, save it here, then start campaigns from it.'
          : 'A template is a design worth keeping. Nobody has saved one yet.'}
      </p>
      {!mayWrite ? null : (
        <Link
          className="mt-4 inline-block rounded-pill bg-brand px-5 py-2.5 font-bold text-[14px] text-brand-contrast"
          to="/compose/template/new"
        >
          Compose one
        </Link>
      )}
    </div>
  )
}
